import { useState, useEffect } from 'react'
import { Devices } from '../api'
import type { DeviceSummary } from '../api'
import AppHeader from '../components/AppHeader'

type SortKey = 'hostname' | 'last_seen'
type SortDir = 'asc' | 'desc'

interface Props {
  onNavigate: (target: string) => void
  onLogout: () => void
}

function fromUnix(ts: number | null | undefined): Date | null {
  return ts != null ? new Date(ts * 1000) : null
}

function timeAgo(iso: string | null | undefined, from = Date.now()): string {
  if (!iso) return '—'
  const diffMs = from - new Date(iso).getTime()
  const diffS = Math.floor(diffMs / 1000)
  if (diffS < 60)  return `${diffS}s ago`
  const diffM = Math.floor(diffS / 60)
  if (diffM < 60)  return `${diffM}m ago`
  const diffH = Math.floor(diffM / 60)
  if (diffH < 24)  return `${diffH}h ago`
  return `${Math.floor(diffH / 24)}d ago`
}

function fmtTimeShort(iso: string | Date | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  })
}

function staleness(iso: string | Date | null | undefined): 'fresh' | 'stale' | 'dead' {
  if (!iso) return 'dead'
  const diffMs = Date.now() - new Date(iso).getTime()
  // Tightened thresholds: pusher writes the .last_seen sentinel every 10 s, so
  // anything older than 90 s is either offline or pushing failed.
  if (diffMs <  90 * 1000)     return 'fresh'
  if (diffMs <   5 * 60 * 1000) return 'stale'
  return 'dead'
}

// Primary freshness signal is `last_seen_at` (sentinel ticks every 10 s); when
// null (legacy device or offline since this landed), fall back to the latest
// run start so the dot still surfaces something useful.
function freshnessSource(d: DeviceSummary): Date | null {
  if (d.lastSeenAt) return new Date(d.lastSeenAt as unknown as string)
  return fromUnix(d.latestRunStartedAt)
}

function StatusDot({ iso }: { iso: string | Date | null | undefined }) {
  const s = staleness(iso)
  const cls =
    s === 'fresh' ? 'bg-emerald-500 animate-pulse-err' :
    s === 'stale' ? 'bg-gold' :
    'bg-stone-300'
  return <span className={`w-2 h-2 rounded-full inline-block shrink-0 ${cls}`} />
}

const GRID = '[grid-template-columns:1.5fr_1fr_1fr_auto]'

function ColHeader({
  label, sortKey, current, dir, onSort, center = false,
}: {
  label: string; sortKey: SortKey; current: SortKey; dir: SortDir
  onSort: (k: SortKey) => void; center?: boolean
}) {
  const active = sortKey === current
  return (
    <button
      onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 w-full font-mono text-[10px] tracking-[0.15em] uppercase
        transition-colors cursor-pointer
        ${center ? 'justify-center' : 'text-left'}
        ${active ? 'text-bear-bright' : 'text-stone-500 hover:text-stone-900'}`}
    >
      {label}
      <svg
        className={`w-2 h-2 shrink-0 transition-all duration-150
          ${active ? 'opacity-100' : 'opacity-0'}
          ${active && dir === 'asc' ? 'rotate-180' : ''}`}
        viewBox="0 0 10 8" fill="currentColor"
      >
        <path d="M5 0l5 8H0z" />
      </svg>
    </button>
  )
}

function MobileCard({
  device, onTimeline, onCaptures,
}: {
  device: DeviceSummary; onTimeline: () => void; onCaptures: () => void
}) {
  const seen = freshnessSource(device)
  const s = staleness(seen)
  const accentColor =
    s === 'fresh' ? 'border-l-emerald-500/70' :
    s === 'stale' ? 'border-l-gold/60' :
    'border-l-stone-200'
  const lastCatch = fromUnix(device.latestCaptureEndTimestamp)

  return (
    <div className={`bg-white rounded-xl border border-stone-200 border-l-4 ${accentColor} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <StatusDot iso={seen} />
        <button
          onClick={onTimeline}
          className="font-mono font-semibold text-sm text-stone-900 tracking-wide
            hover:text-bear transition-colors truncate text-left cursor-pointer flex-1 min-w-0"
        >
          {device.hostname}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-4">
        <div>
          <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-400 mb-0.5">Last seen</p>
          {seen != null ? (
            <p className="font-mono text-[10px] text-stone-400">{timeAgo(seen.toISOString())}</p>
          ) : <p className="font-mono text-xs text-stone-400">—</p>}
        </div>
        <div>
          <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-400 mb-0.5">Last capture</p>
          {lastCatch != null ? (
            <p className="font-mono text-[10px] text-stone-400">{timeAgo(lastCatch.toISOString())}</p>
          ) : <p className="font-mono text-xs text-stone-400">—</p>}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onTimeline}
          className="flex-1 font-mono text-[10px] tracking-[0.12em] uppercase px-3 py-2 rounded-lg
            text-stone-700 border border-stone-200 hover:border-stone-400 hover:text-stone-900
            transition-colors cursor-pointer">
          Timeline
        </button>
        <button onClick={onCaptures}
          className="flex-1 font-mono text-[10px] tracking-[0.12em] uppercase px-3 py-2 rounded-lg
            text-bear border border-bear/30 hover:border-bear/60
            transition-colors cursor-pointer">
          Captures
        </button>
      </div>
    </div>
  )
}

function DeviceRow({
  device, onTimeline, onCaptures,
}: {
  device: DeviceSummary; onTimeline: () => void; onCaptures: () => void
}) {
  const seen = freshnessSource(device)
  const s = staleness(seen)
  const accentColor =
    s === 'fresh' ? 'border-l-emerald-500/60' :
    s === 'stale' ? 'border-l-gold/50' :
    'border-l-stone-200'
  const lastCatch = fromUnix(device.latestCaptureEndTimestamp)

  return (
    <div className={`grid items-center gap-3 px-4 py-3.5
      border-b border-stone-100 last:border-0
      hover:bg-stone-50 transition-colors
      border-l-2 ${accentColor} ${GRID}`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <StatusDot iso={seen} />
        <button onClick={onTimeline}
          className="font-mono font-semibold text-sm text-stone-900 tracking-wide hover:text-bear
            transition-colors truncate text-left cursor-pointer">
          {device.hostname}
        </button>
      </div>

      <div className="min-w-0">
        {seen != null ? (
          <>
            <span className="font-mono text-xs text-stone-700 block truncate">{fmtTimeShort(seen)}</span>
            <span className="font-mono text-[10px] text-stone-400">{timeAgo(seen.toISOString())}</span>
          </>
        ) : <span className="font-mono text-xs text-stone-400">—</span>}
      </div>

      <div className="min-w-0">
        {lastCatch != null ? (
          <>
            <span className="font-mono text-xs text-stone-700 block truncate">{fmtTimeShort(lastCatch)}</span>
            <span className="font-mono text-[10px] text-stone-400">{timeAgo(lastCatch.toISOString())}</span>
          </>
        ) : <span className="font-mono text-xs text-stone-400">—</span>}
      </div>

      <div className="flex items-center gap-2">
        <button onClick={onTimeline}
          className="font-mono text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 rounded
            text-stone-700 border border-stone-200 hover:border-stone-400 hover:text-stone-900
            transition-colors cursor-pointer whitespace-nowrap">
          Timeline
        </button>
        <button onClick={onCaptures}
          className="font-mono text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 rounded
            text-bear border border-bear/30 hover:border-bear/60
            transition-colors cursor-pointer whitespace-nowrap">
          Captures
        </button>
      </div>
    </div>
  )
}

export default function StatusPage({ onNavigate, onLogout }: Props) {
  const [devices, setDevices]     = useState<DeviceSummary[]>([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState<string | null>(null)
  const [lastFetch, setLastFetch] = useState<Date | null>(null)
  const [sortKey, setSortKey]     = useState<SortKey>('last_seen')
  const [sortDir, setSortDir]     = useState<SortDir>('desc')
  const [query, setQuery]         = useState('')
  const [now, setNow]             = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(id)
  }, [])

  function load() {
    Devices.listDevices()
      .then(r => { setDevices(r.devices); setLoading(false); setLastFetch(new Date()) })
      .catch((err: Error) => { setError(err.message); setLoading(false) })
  }

  useEffect(() => {
    load()
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'hostname' ? 'asc' : 'desc')
    }
  }

  const needle = query.trim().toLowerCase()
  const shown = devices.filter(d => !needle || d.hostname.toLowerCase().includes(needle))
  const sorted = [...shown].sort((a, b) => {
    let cmp = 0
    if (sortKey === 'hostname') {
      cmp = a.hostname.localeCompare(b.hostname)
    } else if (sortKey === 'last_seen') {
      // Never-seen devices compare as 0 so they sink to the bottom under 'desc'.
      const aTs = freshnessSource(a)?.getTime() ?? 0
      const bTs = freshnessSource(b)?.getTime() ?? 0
      cmp = aTs - bTs
    }
    return sortDir === 'asc' ? cmp : -cmp
  })

  function viewTimeline(hostname: string) { onNavigate(`#device/${encodeURIComponent(hostname)}/timeline`) }
  function viewCaptures(hostname: string) { onNavigate(`#captures?hostname=${encodeURIComponent(hostname)}`) }

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader active="status" onNavigate={onNavigate} onLogout={onLogout} />

      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 shrink-0">
            {needle ? `${shown.length} / ${devices.length}` : devices.length} device{devices.length !== 1 ? 's' : ''}
            {lastFetch && <span className="hidden sm:inline">{` · updated ${timeAgo(lastFetch.toISOString(), now)}`}</span>}
          </p>
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="search devices…"
            aria-label="Search devices"
            className="flex-1 min-w-0 max-w-xs px-3 py-1.5 font-mono text-xs bg-white border border-stone-200
              rounded-lg text-stone-700 placeholder-stone-400
              focus:outline-none focus:border-bear/40 transition-colors"
          />
          <button onClick={load}
            className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500
              hover:text-bear transition-colors px-2 py-1 rounded cursor-pointer">
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center py-24">
            <p className="font-mono text-xs text-coral">Error: {error}</p>
          </div>
        )}
        {!loading && !error && devices.length === 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 px-4 py-16 text-center">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">No devices found</p>
          </div>
        )}

        {!loading && !error && devices.length > 0 && sorted.length === 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 px-4 py-16 text-center">
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">
              No devices match “{query.trim()}”
            </p>
          </div>
        )}

        {!loading && !error && sorted.length > 0 && (
          <>
            <div className="md:hidden space-y-2.5">
              {sorted.map(d => (
                <MobileCard key={d.hostname} device={d}
                  onTimeline={() => viewTimeline(d.hostname)}
                  onCaptures={() => viewCaptures(d.hostname)} />
              ))}
            </div>

            <div className="hidden md:block bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className={`grid items-center gap-3 px-4 py-2.5
                border-b border-stone-200 bg-stone-50 border-l-2 border-l-transparent ${GRID}`}>
                <ColHeader label="Device"     sortKey="hostname"  current={sortKey} dir={sortDir} onSort={handleSort} />
                <ColHeader label="Last seen"  sortKey="last_seen" current={sortKey} dir={sortDir} onSort={handleSort} />
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500">Last capture</span>
                <span />
              </div>
              {sorted.map(d => (
                <DeviceRow key={d.hostname} device={d}
                  onTimeline={() => viewTimeline(d.hostname)}
                  onCaptures={() => viewCaptures(d.hostname)} />
              ))}
            </div>
          </>
        )}

        {lastFetch && (
          <p className="text-center font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 py-8">
            Auto-refreshes every 30s
          </p>
        )}
      </div>
    </div>
  )
}
